import { Box, Divider, Stack, useTheme } from "@mui/material";
import { FoodsIcon, SectionTab, SectionTabs } from "@foodpilot/foods";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "./Header";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { MainContentLayout } from "../Layout/MainContentLayout";
import { ActionPlanMainTabs, OneInLocalStorage } from "src/services/OneFromLocalStorage";
import { Loading } from "../utils/Loading";
import { useQueryClient } from "react-query";
import { getOneActionPlanKey } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { ActionLoadingState } from "./Common/ActionLoadingState";

export type MainLayoutProps = {
    selectedSection?: ActionPlanMainTabs;
};

type ActionssSectionTab = SectionTab & { title?: string };
type ActionsSections = Partial<Record<ActionPlanMainTabs, ActionssSectionTab>>;

export const MainLayout = (props: MainLayoutProps) => {
    const { selectedSection } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { urls, actionPlan, isFetched, isRefetching, actionPlanSavingStatus } = useActionsContext();

    if (isRefetching === true) {
        return <Loading />;
    }

    if (isFetched === false) return <Loading />;

    const shouldCreateActionPlan = actionPlan === null;

    if (shouldCreateActionPlan && selectedSection !== undefined) {
        const url = urls.noActionPlan();
        return <Navigate to={url} replace />;
    }
    if (shouldCreateActionPlan === false && selectedSection === undefined) {
        const url = urls.trajectory();
        return <Navigate to={url} replace />;
    }

    const sections: ActionsSections = {
        trajectory: {
            name: t("actionPlan.trajectory.tab"),
            icon: <FoodsIcon icon="graph" size={3} />,
            disabled: selectedSection === undefined,
            onClick: () => {
                OneInLocalStorage.setItem("actionPlan-main-tabs", "trajectory");
                queryClient.invalidateQueries([getOneActionPlanKey]);
                const url1 = urls.trajectory();
                navigate(url1);
                return;
            },
        },
        planning: {
            name: t("actionPlan.tabTitle"),
            icon: <FoodsIcon icon="actionPlan" size={3} />,
            disabled: selectedSection === undefined,
            onClick: () => {
                OneInLocalStorage.setItem("actionPlan-main-tabs", "planning");
                const url = urls.planning();
                navigate(url);
            },
        },
    };

    const selectedTab = Object.keys(sections).indexOf(selectedSection ?? "");
    return (
        <Box component="div" sx={{ marginLeft: "auto", marginRight: "auto" }}>
            <Box
                sx={{
                    // width: "100%",
                    background: "#fff",
                    padding: "28px 52px",
                    paddingBottom: 0,
                    position: "relative",
                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    gap="16px"
                    sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                    <Header />
                </Stack>

                <Divider sx={{ marginTop: "28px" }} />
                <SectionTabs
                    tabs={Object.values(sections)}
                    value={selectedTab}
                    renderComponents={false}
                    sx={{
                        position: "relative",
                        bottom: "-1px",
                    }}
                />
            </Box>
            {actionPlanSavingStatus.isSaving ?
                <MainContentLayout>
                    <ActionLoadingState />
                </MainContentLayout>
            :   <MainContentLayout />}
        </Box>
    );
};
